import {
  AddUniversal,
  CloseUniversal,
  TickMarkOutlined,
  TimerUniversal,
  UndoUniversal,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { targetUserTypes } from 'data/trident-types';
import i18n from 'i18n-js';
import React, { useEffect, useMemo, useState } from 'react';

import SegPWidget from 'omega-rtc/components/form/ucd/common/SegPWidget';
import {
  AudienceDeeplink,
  AudiencePersonaVariable,
  AudienceSourceType,
} from 'omega-rtc/types/ts/content-config';

import {
  formatDateTime,
  formatExpiry,
  isPrestoViewError,
  isReservedDeeplinkName,
  localeSorter,
  nameFromEmail,
} from './adapters';
import { AudienceSourceCell, AudienceSourceLinks } from './AudienceSourceLinks';
import DiffField from './DiffField';
import { editChangesText } from './editChanges';
import { contentConfigModalStyle } from './theme';

const { TextArea } = Input;

const audienceText = (key, options = {}) =>
  i18n.t(`ucd_content_config.audiences.${key}`, options);

const sourceTypeLabel = (type) => {
  if (type === AudienceSourceType.Segment) return audienceText('source_types.segment');
  if (type === AudienceSourceType.Table) return audienceText('source_types.table');
  return '—';
};

const statusTag = (status) => {
  if (status === 'pending') {
    return <Tag color="processing">{audienceText('statuses.pending')}</Tag>;
  }
  return <Tag color="success">{audienceText('statuses.active')}</Tag>;
};

interface FormFieldProps {
  children: React.ReactNode;
  error?: React.ReactNode;
  help?: React.ReactNode;
  id: string;
  label: React.ReactNode;
  labelExtra?: React.ReactNode;
  labelHint?: React.ReactNode;
  required?: boolean;
}

const FormField = ({
  children,
  error,
  help,
  id,
  label,
  labelExtra,
  labelHint,
  required = false,
}: FormFieldProps) => {
  const labelElement = (
    <label className="cc-field-label" htmlFor={id}>
      <Flex align="center" gap={8} wrap="wrap">
        <Typography.Body level={2} strong>
          {label}
          {required ? ' *' : ''}
        </Typography.Body>
        {labelHint && (
          <Typography.Metadata type="secondary">
            ({labelHint})
          </Typography.Metadata>
        )}
      </Flex>
    </label>
  );

  return (
    <div className="cc-field">
      {labelExtra ? (
        // Interactive extras sit beside the label, not inside it, so clicking
        // them never activates the labelled control.
        <Flex align="baseline" gap={16} wrap="wrap">
          {labelElement}
          {labelExtra}
        </Flex>
      ) : labelElement}
      {children}
      {(error || help) && (
        <Typography.Metadata
          id={`${id}-description`}
          type={error ? 'danger' : 'secondary'}
        >
          {error || help}
        </Typography.Metadata>
      )}
    </div>
  );
};

export const AudienceList = ({
  canCreate,
  countries,
  listError,
  listTab,
  loading,
  onCreate,
  onOpenDetail,
  onRetry,
  rows,
  scopeFilter,
  setListTab,
  setScopeFilter,
  setSourceFilter,
  sourceFilter,
}) => {
  const columns = [
    {
      title: audienceText('columns.name'),
      dataIndex: 'name',
      sorter: localeSorter('name'),
      width: 360,
      render: (name, row) => (
        <Flex align="center" gap={6}>
          <Typography.Body level={2}>{name}</Typography.Body>
          {row.status === 'pending' && (
            <Tooltip title={audienceText('filters.pending')}>
              <TimerUniversal className="cc-pending-icon" />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    {
      title: audienceText('columns.scope'),
      dataIndex: 'scopeLabel',
      sorter: localeSorter('scopeLabel'),
      width: 126,
    },
    {
      title: audienceText('columns.source_type'),
      dataIndex: 'type',
      width: 117,
      render: (type) => <Tag>{sourceTypeLabel(type)}</Tag>,
    },
    {
      title: audienceText('columns.source'),
      dataIndex: 'source',
      ellipsis: true,
      width: 324,
      render: (source, row) => (
        <AudienceSourceCell type={row.type} source={source} />
      ),
    },
    {
      title: audienceText('columns.updated_by'),
      dataIndex: 'updatedBy',
      width: 162,
      render: (user) => nameFromEmail(user) || '—',
    },
    {
      title: audienceText('columns.updated_on'),
      dataIndex: 'updatedAt',
      width: 198,
      render: formatDateTime,
    },
  ];

  const scopeOptions = [
    { value: 'all', label: audienceText('filters.all_scopes') },
    { value: 'regional', label: audienceText('scope.regional') },
    ...countries.map((country) => ({ value: country.id, label: country.name })),
  ];

  return (
    <Flex vertical gap="middle">
      <Flex align="center" gap="middle" wrap="wrap">
        <Typography.Title level={4} className="cc-title-reset">
          {audienceText('title')}
        </Typography.Title>
        <Tooltip
          title={canCreate ? '' : audienceText('permissions.create')}
        >
          <Button
            type="primary"
            icon={<AddUniversal />}
            disabled={!canCreate}
            onClick={onCreate}
          >
            {audienceText('actions.create')}
          </Button>
        </Tooltip>
      </Flex>

      <Tabs
        type="pill"
        activeKey={listTab}
        onChange={setListTab}
        items={[
          { key: 'all', label: audienceText('filters.all') },
          { key: 'pending', label: audienceText('filters.pending') },
        ]}
      />

      <Flex align="center" gap={12} wrap="wrap" className="cc-list-controls">
        <label htmlFor="audience-scope-filter">
          <Typography.Body level={2} type="secondary">
            {audienceText('filters.scope')}
          </Typography.Body>
        </label>
        <Select
          id="audience-scope-filter"
          value={scopeFilter}
          onChange={setScopeFilter}
          options={scopeOptions}
          className="cc-country-select"
        />
        <label htmlFor="audience-source-filter">
          <Typography.Body level={2} type="secondary">
            {audienceText('filters.source_type')}
          </Typography.Body>
        </label>
        <Select
          id="audience-source-filter"
          value={sourceFilter}
          onChange={setSourceFilter}
          className="cc-vertical-select"
          options={[
            { value: 'all', label: audienceText('filters.all_sources') },
            {
              value: AudienceSourceType.Segment,
              label: audienceText('source_types.segment'),
            },
            {
              value: AudienceSourceType.Table,
              label: audienceText('source_types.table'),
            },
          ]}
        />
      </Flex>

      {listError && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={(
            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
              <span>{audienceText('errors.list')}</span>
              <Button size="small" onClick={onRetry}>
                {audienceText('actions.retry')}
              </Button>
            </Flex>
          )}
        />
      )}

      <Table
        rowKey="rowKey"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        scroll={{ x: 1287 }}
        onRow={(row) => ({
          className: 'cc-row-clickable',
          onClick: () => onOpenDetail(row.rowKey),
        })}
        locale={{
          emptyText: listTab === 'pending'
            ? audienceText('empty.pending')
            : audienceText('empty.all'),
        }}
      />
    </Flex>
  );
};

const AudienceVariableEditor = ({
  disabled,
  errors,
  deeplink,
  onDeeplinkKeysChange,
  onDeeplinkTemplateChange,
  onVariableDescriptionChange,
  onVariableKeysChange,
  options,
  personaVariables,
}) => {
  const selectedPersonaKeys = personaVariables.map((variable) => variable.name);
  const selectedDeeplinkKeys = (deeplink && deeplink.names) || [];
  const selectOptions = options
    .filter((option) => option.key !== 'user_id')
    .map((option) => ({
      value: option.key,
      label: option.key,
    }));
  // Reserved tracking params are stamped by UCD, so they can't be deep-link variables.
  const deeplinkSelectOptions = selectOptions.filter((option) =>
    !isReservedDeeplinkName(option.value)
    || selectedDeeplinkKeys.includes(option.value));

  return (
    <React.Fragment>
      <Card bordered={false} className="cc-section-gap">
        <FormField
          id="audience-persona-variables"
          label={audienceText('fields.persona_variables')}
          required
          error={errors.personaVariables}
          help={audienceText('help.persona_variables')}
        >
          <Select
            id="audience-persona-variables"
            mode="multiple"
            value={selectedPersonaKeys}
            onChange={onVariableKeysChange}
            options={selectOptions}
            optionFilterProp="label"
            allowClear
            disabled={disabled}
            className="cc-full-width"
            aria-required
            aria-invalid={Boolean(errors.personaVariables)}
            aria-describedby="audience-persona-variables-description"
          />
        </FormField>

        {personaVariables.length > 0 && (
          <div className="cc-audience-variable-list">
            {personaVariables.map((variable) => {
              const descriptionError = errors.personaVariableDescriptions
                && errors.personaVariableDescriptions[variable.name];
              return (
                <div
                  key={variable.name}
                  className="cc-audience-variable-row"
                >
                  <label
                    className="cc-field-label"
                    htmlFor={`audience-persona-description-${variable.name}`}
                  >
                    <Typography.Body level={2} strong>
                      {variable.name}
                      {' *'}
                    </Typography.Body>
                  </label>
                  <div>
                    <Input
                      id={`audience-persona-description-${variable.name}`}
                      data-audience-field="personaVariableDescriptions"
                      value={variable.description}
                      onChange={(event) =>
                        onVariableDescriptionChange(variable.name, event.target.value)}
                      disabled={disabled}
                      placeholder={audienceText('placeholders.persona_variable_description')}
                      aria-required
                      aria-invalid={Boolean(descriptionError)}
                      aria-describedby={
                        descriptionError
                          ? `audience-persona-description-${variable.name}-description`
                          : undefined
                      }
                    />
                    {descriptionError && (
                      <Typography.Metadata
                        id={`audience-persona-description-${variable.name}-description`}
                        type="danger"
                      >
                        {descriptionError}
                      </Typography.Metadata>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card bordered={false} className="cc-section-gap">
        <FormField
          id="audience-deeplink-variables"
          label={audienceText('fields.deeplink_variables')}
          error={errors.deeplinkNames}
          help={audienceText('help.deeplink_variables')}
        >
          <Select
            id="audience-deeplink-variables"
            mode="multiple"
            value={selectedDeeplinkKeys}
            onChange={onDeeplinkKeysChange}
            options={deeplinkSelectOptions}
            optionFilterProp="label"
            allowClear
            disabled={disabled}
            className="cc-full-width"
            aria-invalid={Boolean(errors.deeplinkNames)}
            aria-describedby="audience-deeplink-variables-description"
          />
        </FormField>
        <FormField
          id="audience-deep-link-template"
          label={audienceText('fields.deep_link_template')}
          required={selectedDeeplinkKeys.length > 0}
          error={errors.deepLinkTemplate}
          help={audienceText('help.deep_link_template')}
        >
          <Input
            id="audience-deep-link-template"
            value={(deeplink && deeplink.deepLinkTemplate) || ''}
            onChange={(event) => onDeeplinkTemplateChange(event.target.value)}
            disabled={disabled || selectedDeeplinkKeys.length === 0}
            placeholder={audienceText('placeholders.deep_link_template', {
              parameters: selectedDeeplinkKeys.length
                ? selectedDeeplinkKeys
                  .map((name) => `${name}={{${name}}}`)
                  .join('&')
                : 'value={{variable}}',
            })}
            aria-required={selectedDeeplinkKeys.length > 0}
            aria-invalid={Boolean(errors.deepLinkTemplate)}
            aria-describedby="audience-deep-link-template-description"
          />
        </FormField>
      </Card>
    </React.Fragment>
  );
};

export const AudienceForm = ({
  columnsReady = false,
  countries,
  errors,
  form,
  onChange,
  onDeeplinkKeysChange,
  onDeeplinkTemplateChange,
  onResolveTable,
  onVariableDescriptionChange,
  onVariableKeysChange,
  readOnly = false,
  selectableColumnOptions,
  tableInfo,
  tableResolved,
  tableSourceEnabled = false,
}) => {
  const isTable = form.type === AudienceSourceType.Table;
  const variablesReady = tableResolved || columnsReady;
  const isSegment = form.type === AudienceSourceType.Segment;
  const tableError = tableInfo && tableInfo.error;
  const safeColumnOptions = [
    ...selectableColumnOptions,
    ...(readOnly ? [
      ...form.personaVariables.map((variable) => ({
        key: variable.name,
        desc: variable.description,
      })),
      ...(((form.deeplink && form.deeplink.names) || []).map((name) => ({
        key: name,
        desc: name,
      }))),
    ].filter((candidate, index, all) =>
      !selectableColumnOptions.some((option) => option.key === candidate.key)
      && all.findIndex((option) => option.key === candidate.key) === index) : []),
  ].filter((option) => option.key !== 'user_id');
  const scopeOptions = [
    { value: 'regional', label: audienceText('scope.regional') },
    ...countries.map((country) => ({ value: country.id, label: country.name })),
  ];
  const sourceTypeOptions = [
    {
      value: AudienceSourceType.Segment,
      label: audienceText('source_types.segment'),
    },
    ...(
      tableSourceEnabled || isTable
        ? [{
          value: AudienceSourceType.Table,
          label: audienceText('source_types.table'),
        }]
        : []
    ),
  ];
  // A Presto-view source is a problem with the Source value itself, so it is
  // reported on that field rather than as a retryable lookup failure.
  const prestoViewError = isPrestoViewError(tableError);
  const sourceError = errors.source
    || (prestoViewError ? audienceText('errors.table_presto_view') : undefined);
  let tableMessage = '';
  if (tableError && !prestoViewError) {
    tableMessage = {
      400: audienceText('errors.table_400'),
      404: audienceText('errors.table_404'),
      422: audienceText('errors.table_422'),
      503: audienceText('errors.table_503'),
    }[tableError.status] || audienceText('errors.table_unknown');
  }

  return (
    <Flex vertical gap="middle">
      <Card bordered={false}>
        <div className="cc-audience-form-grid">
          <FormField
            id="audience-name"
            label={audienceText('fields.name')}
            required
            error={errors.name}
          >
            <Input
              id="audience-name"
              data-audience-field="name"
              value={form.name}
              onChange={(event) => onChange('name', event.target.value)}
              disabled={readOnly}
              aria-invalid={Boolean(errors.name)}
              aria-describedby="audience-name-description"
            />
          </FormField>
          <FormField
            id="audience-scope"
            label={audienceText('fields.scope')}
            labelHint={
              form.isDraft
                ? audienceText('help.scope')
                : audienceText('help.scope_locked')
            }
            required
            error={errors.country}
          >
            <Select
              id="audience-scope"
              data-audience-field="country"
              value={form.country}
              onChange={(value) => onChange('country', value)}
              options={scopeOptions}
              disabled={readOnly || !form.isDraft}
              className="cc-full-width"
              aria-invalid={Boolean(errors.country)}
              aria-describedby={
                errors.country ? 'audience-scope-description' : undefined
              }
            />
          </FormField>
          <FormField
            id="audience-source-type"
            label={audienceText('fields.source_type')}
            required
            error={errors.type}
          >
            <Select
              id="audience-source-type"
              data-audience-field="type"
              value={form.type}
              onChange={(value) => onChange('type', value)}
              disabled={readOnly}
              className="cc-full-width"
              options={sourceTypeOptions}
              aria-invalid={Boolean(errors.type)}
              aria-describedby="audience-source-type-description"
            />
          </FormField>
          <FormField
            id="audience-source"
            label={audienceText('fields.source')}
            required
            error={sourceError}
            labelExtra={
              <AudienceSourceLinks type={form.type} source={form.source} />
            }
          >
            {isSegment && !readOnly ? (
              <div
                id="audience-source"
                data-audience-field="source"
                aria-invalid={Boolean(sourceError)}
                aria-describedby="audience-source-description"
              >
                <SegPWidget
                  targetUserType={targetUserTypes.pax}
                  onChange={(value?: string) => onChange('source', value || '')}
                  onSelect={() => {}}
                  segmentVal={form.source}
                  maxVisible={30}
                  enabledSegmentNSFormat
                  showNsDropdown
                />
              </div>
            ) : (
              <Flex gap={8}>
                <Input
                  id="audience-source"
                  data-audience-field="source"
                  value={form.source}
                  onChange={(event) => onChange('source', event.target.value)}
                  disabled={readOnly}
                  aria-invalid={Boolean(sourceError)}
                  aria-describedby="audience-source-description"
                />
                {isTable && !readOnly && (
                  <Button
                    onClick={onResolveTable}
                    loading={Boolean(tableInfo && tableInfo.isLoading)}
                    disabled={!form.source}
                  >
                    {form.isDraft
                      ? audienceText('actions.load_columns')
                      : audienceText('actions.reload_columns')}
                  </Button>
                )}
              </Flex>
            )}
          </FormField>
        </div>
        <FormField
          id="audience-description"
          label={audienceText('fields.description')}
          required
          error={errors.description}
        >
          <TextArea
            id="audience-description"
            data-audience-field="description"
            value={form.description}
            onChange={(event) => onChange('description', event.target.value)}
            disabled={readOnly}
            autoSize={{ minRows: 3, maxRows: 8 }}
            aria-invalid={Boolean(errors.description)}
            aria-describedby="audience-description-description"
          />
        </FormField>
      </Card>

      {isTable && !readOnly && (
        <div aria-live="polite">
          {tableError && !prestoViewError && (
            <Alert
              type="error"
              showIcon
              message={tableMessage}
              action={(
                <Button size="small" onClick={onResolveTable}>
                  {audienceText('actions.retry')}
                </Button>
              )}
            />
          )}
          {columnsReady && !tableResolved && !tableError && (
            <Alert
              type="neutral"
              showIcon
              message={audienceText('messages.columns_saved')}
            />
          )}
          {tableResolved && !tableError && (
            <Alert
              type="success"
              showIcon
              message={audienceText('messages.columns_loaded', {
                count: safeColumnOptions.length,
              })}
            />
          )}
        </div>
      )}

      {isTable && (readOnly || variablesReady) && (
        <AudienceVariableEditor
          disabled={readOnly}
          errors={errors}
          deeplink={form.deeplink}
          onDeeplinkKeysChange={onDeeplinkKeysChange}
          onDeeplinkTemplateChange={onDeeplinkTemplateChange}
          onVariableDescriptionChange={onVariableDescriptionChange}
          onVariableKeysChange={onVariableKeysChange}
          options={safeColumnOptions}
          personaVariables={form.personaVariables}
        />
      )}
    </Flex>
  );
};

const variablesForDiff = (variables: AudiencePersonaVariable[] = []) => variables
  .map((variable) => [
    variable.name,
    variable.description,
  ].filter(Boolean).join(' · '))
  .sort()
  .join('\n');

const deeplinkForDiff = (deeplink: Partial<AudienceDeeplink> = {}) => [
  ...(deeplink.names || []).map((name) => name).sort(),
  deeplink.deepLinkTemplate || '',
].filter(Boolean).join('\n');

export const AudienceDiffModal = ({
  active,
  busy,
  canRecall,
  canReview,
  onApprove,
  onCancel,
  onEditChanges,
  onRecall,
  onReject,
  open,
  pending,
  scopeLabel,
}) => {
  const [rejectComment, setRejectComment] = useState('');
  useEffect(() => {
    if (open) setRejectComment('');
  }, [open]);

  const current = active || {
    country: '',
    deeplink: {
      names: [],
      deepLinkTemplate: '',
    },
    description: '',
    name: '',
    personaVariables: [],
    source: '',
    type: '',
  };
  const activeSide = {
    activeLabel: active
      ? audienceText('statuses.active')
      : audienceText('diff.current'),
    activeTagColor: active ? 'error' : undefined,
    pendingLabel: audienceText('statuses.pending'),
  };

  return (
    <Modal
      title={audienceText('diff.title')}
      open={open}
      onCancel={onCancel}
      width={1100}
      style={contentConfigModalStyle}
      className="cc-modal"
      footer={(
        <Flex justify="flex-end" gap={8} wrap="wrap">
          {canRecall && (
            <React.Fragment>
              <Button icon={<UndoUniversal />} danger loading={busy} onClick={onRecall}>
                {audienceText('actions.recall')}
              </Button>
              <Button loading={busy} onClick={onEditChanges}>
                {editChangesText('action')}
              </Button>
            </React.Fragment>
          )}
          {canReview && (
            <React.Fragment>
              <Button
                type="primary"
                danger
                icon={<CloseUniversal />}
                loading={busy}
                disabled={!rejectComment.trim()}
                onClick={() => onReject(rejectComment.trim())}
              >
                {audienceText('actions.reject')}
              </Button>
              <Button
                type="primary"
                icon={<TickMarkOutlined />}
                loading={busy}
                onClick={onApprove}
              >
                {audienceText('actions.approve')}
              </Button>
            </React.Fragment>
          )}
        </Flex>
      )}
    >
      <div className="cc-diff">
        {!canRecall && !canReview && (
          <Alert
            type="neutral"
            showIcon
            message={audienceText('permissions.review')}
          />
        )}
        <DiffField
          {...activeSide}
          label={audienceText('fields.name')}
          activeVal={current.name}
          pendingVal={pending && pending.name}
        />
        <DiffField
          {...activeSide}
          label={audienceText('fields.description')}
          activeVal={current.description}
          pendingVal={pending && pending.description}
        />
        <DiffField
          {...activeSide}
          label={audienceText('fields.scope')}
          activeVal={active ? scopeLabel(active.country) : ''}
          pendingVal={pending ? scopeLabel(pending.country) : ''}
        />
        <DiffField
          {...activeSide}
          label={audienceText('fields.source_type')}
          activeVal={sourceTypeLabel(current.type)}
          pendingVal={sourceTypeLabel(pending && pending.type)}
        />
        <DiffField
          {...activeSide}
          label={audienceText('fields.source')}
          activeVal={current.source}
          pendingVal={pending && pending.source}
        />
        {(current.type === AudienceSourceType.Table
          || (pending && pending.type === AudienceSourceType.Table)) && (
          <React.Fragment>
            <DiffField
              {...activeSide}
              label={audienceText('fields.persona_variables')}
              mode="line"
              activeVal={variablesForDiff(current.personaVariables)}
              pendingVal={variablesForDiff(pending && pending.personaVariables)}
            />
            <DiffField
              {...activeSide}
              label={audienceText('fields.deeplink')}
              mode="line"
              activeVal={deeplinkForDiff(current.deeplink)}
              pendingVal={deeplinkForDiff(pending && pending.deeplink)}
            />
          </React.Fragment>
        )}
        {canReview && (
          <FormField
            id="audience-rejection-comment"
            label={audienceText('fields.rejection_comment')}
            help={audienceText('help.rejection_comment')}
          >
            <TextArea
              id="audience-rejection-comment"
              value={rejectComment}
              onChange={(event) => setRejectComment(event.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
          </FormField>
        )}
      </div>
    </Modal>
  );
};

export const AudienceDetail = ({
  active,
  busy,
  canRecall,
  canReview,
  columnsReady,
  countries,
  errors,
  form,
  hasChanges,
  isDraft,
  isEditing,
  onApprove,
  onCancelEdit,
  onChange,
  onDeeplinkKeysChange,
  onDeeplinkTemplateChange,
  onEditChanges,
  onRecall,
  onReject,
  onRequestReject,
  onResolveTable,
  onSubmit,
  onVariableDescriptionChange,
  onVariableKeysChange,
  pending,
  record,
  selectableColumnOptions,
  setShowPending,
  showPending,
  tableInfo,
  tableResolved,
  tableSourceEnabled,
}) => {
  const [showDiff, setShowDiff] = useState(false);
  const countryName = (country) => {
    if (!country) return audienceText('scope.regional');
    return (countries.find((item) => item.id === country) || {}).name || country;
  };
  const hasPending = Boolean(pending);
  const pendingAuthor = hasPending
    ? pending.updatedBy || pending.createdBy
    : '';
  const pendingExpiry = hasPending ? formatExpiry(pending.expiresAt) : '';
  const showingPending = hasPending && (!active || showPending);

  if (!record && !isDraft) {
    return <Empty description={audienceText('empty.selection')} />;
  }

  return (
    <Flex vertical gap="middle">
      {hasPending && !isEditing && (
        <Alert
          type="info"
          showIcon
          className="cc-action-bar"
          message={(
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div className="cc-alert-content">
                <div>
                  {audienceText('messages.showing_version_prefix')}
                  {' '}
                  <strong>
                    {showingPending
                      ? audienceText('statuses.pending')
                      : audienceText('statuses.active')}
                  </strong>
                  {' '}
                  {audienceText('messages.pending_locked')}
                </div>
                <div>
                  {audienceText('messages.pending_author')}
                  {' '}
                  <strong>{pendingAuthor || '—'}</strong>
                  {pendingExpiry ? (
                    <React.Fragment>
                      {' '}
                      {audienceText('messages.pending_expiry')}
                      {' '}
                      <strong>{pendingExpiry}</strong>
                    </React.Fragment>
                  ) : null}
                  .
                </div>
              </div>
              {active && (
                <Button size="small" onClick={() => setShowPending(!showPending)}>
                  {showPending
                    ? audienceText('actions.see_active')
                    : audienceText('actions.see_changes')}
                </Button>
              )}
            </Flex>
          )}
        />
      )}

      <Flex vertical gap={4} className="cc-detail-header">
        <Typography.Title level={4} className="cc-title-reset">
          {(isEditing ? form.name : record && record.name)
            || audienceText('new_title')}
        </Typography.Title>
        {!isDraft && record && (
          <Typography.Metadata type="secondary">
            {audienceText('messages.updated_by', {
              user: nameFromEmail(record.updatedBy || record.createdBy) || '—',
              date: formatDateTime(record.updatedAt),
            })}
          </Typography.Metadata>
        )}
      </Flex>

      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap={12}
        className="cc-action-bar"
      >
        {isDraft ? (
          <Tag color="warning">{audienceText('statuses.draft')}</Tag>
        ) : statusTag(hasPending ? 'pending' : 'active')}
        <Flex gap={8} align="center" wrap="wrap">
          {hasPending && (
            <Button size="small" onClick={() => setShowDiff(true)}>
              {audienceText('actions.show_diff')}
            </Button>
          )}
          {hasPending && canRecall && (
            <React.Fragment>
              <Button size="small" icon={<UndoUniversal />} danger loading={busy} onClick={onRecall}>
                {audienceText('actions.recall')}
              </Button>
              <Button size="small" loading={busy} onClick={onEditChanges}>
                {editChangesText('action')}
              </Button>
            </React.Fragment>
          )}
          {hasPending && canReview && (
            <React.Fragment>
              <Button
                size="small"
                type="primary"
                danger
                icon={<CloseUniversal />}
                loading={busy}
                onClick={onRequestReject}
              >
                {audienceText('actions.reject')}
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<TickMarkOutlined />}
                loading={busy}
                onClick={onApprove}
              >
                {audienceText('actions.approve')}
              </Button>
            </React.Fragment>
          )}
          {isDraft && (
            <Button
              size="small"
              icon={<UndoUniversal />}
              danger
              disabled={busy}
              onClick={onCancelEdit}
            >
              {audienceText('actions.discard')}
            </Button>
          )}
          {!isDraft && isEditing && hasChanges && (
            <Button
              size="small"
              icon={<UndoUniversal />}
              danger
              disabled={busy}
              onClick={onCancelEdit}
            >
              {audienceText('actions.discard_local_edits')}
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            icon={<TickMarkOutlined />}
            loading={busy}
            disabled={!isEditing || (!isDraft && !hasChanges)}
            onClick={onSubmit}
          >
            {audienceText('actions.send_for_approval')}
          </Button>
        </Flex>
      </Flex>

      <AudienceForm
        columnsReady={columnsReady}
        countries={countries}
        errors={isEditing ? errors : {}}
        form={isEditing ? form : {
          ...record,
          country: record.country || 'regional',
          isDraft: false,
        }}
        onChange={onChange}
        onDeeplinkKeysChange={onDeeplinkKeysChange}
        onDeeplinkTemplateChange={onDeeplinkTemplateChange}
        onResolveTable={onResolveTable}
        onVariableDescriptionChange={onVariableDescriptionChange}
        onVariableKeysChange={onVariableKeysChange}
        readOnly={!isEditing}
        selectableColumnOptions={selectableColumnOptions}
        tableInfo={tableInfo}
        tableResolved={tableResolved}
        tableSourceEnabled={tableSourceEnabled}
      />

      {pending && (
        <AudienceDiffModal
          active={active}
          busy={busy}
          canRecall={canRecall}
          canReview={canReview}
          onApprove={() => {
            setShowDiff(false);
            onApprove();
          }}
          onCancel={() => setShowDiff(false)}
          onEditChanges={() => {
            setShowDiff(false);
            onEditChanges();
          }}
          onRecall={() => {
            setShowDiff(false);
            onRecall();
          }}
          onReject={(comment) => {
            setShowDiff(false);
            onReject(comment);
          }}
          open={showDiff}
          pending={pending}
          scopeLabel={countryName}
        />
      )}
    </Flex>
  );
};

export default AudienceDetail;
